import React from 'react'
import { useDispatch } from 'react-redux'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCamera } from '@react-icons/all-files/fi/FiCamera'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiClipboard } from '@react-icons/all-files/fi/FiClipboard'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import { FiFilm } from '@react-icons/all-files/fi/FiFilm'
import { FiMail } from '@react-icons/all-files/fi/FiMail'
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiRadio } from '@react-icons/all-files/fi/FiRadio'
import { FiSearch } from '@react-icons/all-files/fi/FiSearch'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiVideo } from '@react-icons/all-files/fi/FiVideo'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import { SHOW_POPUP } from '../../../store/slices/ui'
import { panelSet } from '../../../store/slices/chatV2'
import * as gtag from '../../../lib/gtag'
import ProductionPopup from '../../DeferredProductionPopup'
import { useOrderFlow } from '../../unified-order/OrderFlowProvider'
import PageShell from '../foundations/PageShell'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import ProductCatalog from '../commerce/ProductCatalog'
import ServiceHero from './ServiceHero'
import ReviewProof from './ReviewProof'
import FAQGroup from './FAQGroup'
import CorporateProposalForm from './CorporateProposalForm'
import styles from './CorporateProductionPage.module.scss'
import localStyles from './LiveStreamingProductionPage.module.scss'

const capabilityIcons = {
  camera: FiCamera,
  audio: FiVolume2,
  switching: FiMonitor,
  signal: FiRadio,
  remote: FiUsers,
  recording: FiFilm
}

const eventTypeIcons = [FiUsers, FiVideo, FiRadio, FiMonitor, FiFilm]

const SectionHeading = ({ eyebrow, title, description, inverse = false, id }) => (
  <div className={`${styles.sectionHeading} ${inverse ? styles.inverseHeading : ''}`.trim()}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2 id={id}>{title}</h2>
    {description && <p>{description}</p>}
  </div>
)

const LiveStreamingProductionPage = ({ bundles, content, reviewData }) => {
  const dispatch = useDispatch()
  const { launchIntent } = useOrderFlow()

  const track = (action, value = 3, sourceSurface) => gtag.event({
    action,
    category: 'orders',
    label: 'leads',
    sourceSurface,
    value
  })

  const openProposal = sourceSurface => {
    track('live_streaming_proposal_open', 15, sourceSurface)
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({
        key: 'live-streaming-production-proposal',
        size: 'big',
        trapFocus: true,
        ariaLabelledBy: 'live-streaming-proposal-modal-title',
        body: (
          <PageShell className={styles.modalShell}>
            <div className={styles.modalForm}>
              <span>Live streaming production</span>
              <h2 id='live-streaming-proposal-modal-title' tabIndex={-1} data-dialog-initial-focus>Tell us about the broadcast</h2>
              <p>Share what is already decided. A producer will help define the technical scope, testing plan, and next step.</p>
              <CorporateProposalForm idPrefix='live-streaming-proposal-modal' mode='streaming' compact />
            </div>
          </PageShell>
        )
      }))
    })
  }

  const openBuilder = sourceSurface => {
    track('live_streaming_builder_open', 3, sourceSurface)
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({ size: 'xl', key: 'prodpop', body: <ProductionPopup /> }))
    })
  }

  const openChat = () => {
    track('live_streaming_chat', 15, 'live_streaming_path_choice')
    dispatch(panelSet(true))
    window.dispatchEvent(new CustomEvent('msr:open-chat'))
  }

  const rating = reviewData?.source?.averageRating
  const reviewCount = reviewData?.source?.totalReviewCount
  const proof = content.proof.map(item => {
    if (item.key === 'rating' && rating) return { ...item, value: `${rating} / 5` }
    if (item.key === 'reviews' && reviewCount) return { ...item, value: Number(reviewCount).toLocaleString('en-US') }
    return item
  })
  const processSteps = content.process.steps.map((step, index) => ({
    ...step,
    icon: [FiSearch, FiClipboard, FiClock, FiCheck][index]
  }))

  return (
    <PageShell className={styles.page}>
      <ServiceHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Live Streaming Production' }
        ]}
        eyebrow={content.hero.eyebrow}
        title={content.hero.title}
        description={content.hero.description}
        image={content.hero.image}
        imageAlt={content.hero.imageAlt}
        stackActionsOnMobile
        primaryAction={{
          label: content.hero.primaryAction,
          icon: FiMail,
          onClick: () => openProposal('live_streaming_hero'),
          channel: 'quote',
          conversionKey: 'live-streaming.hero.proposal',
          salesPath: 'managed'
        }}
        secondaryAction={{
          label: content.hero.secondaryAction,
          icon: FiPhone,
          href: 'tel:+13059744411',
          external: true,
          onClick: () => track('live_streaming_hero_call', 15, 'live_streaming_hero'),
          channel: 'phone',
          conversionKey: 'live-streaming.hero.call',
          salesPath: 'managed'
        }}
        processLabel='Live-streaming production process'
        steps={processSteps}
      />

      <section className={styles.proof} aria-label='Live streaming production proof'>
        <Container className={styles.proofGrid}>
          {proof.map(item => <div key={item.key}><strong>{item.value}</strong><span>{item.label}</span></div>)}
        </Container>
      </section>

      <section className={styles.paths} aria-labelledby='streaming-paths-title'>
        <Container>
          <SectionHeading id='streaming-paths-title' {...content.paths} />
          <div className={styles.pathGrid}>
            <article className={`${styles.pathCard} ${styles.managedPath}`}>
              <span>{content.paths.managed.label}</span>
              <h3>{content.paths.managed.title}</h3>
              <p>{content.paths.managed.description}</p>
              <Action
                size='large'
                icon={FiArrowRight}
                onClick={() => openProposal('live_streaming_path_choice')}
                dataConversionChannel='quote'
                dataConversionKey='live-streaming.path.proposal'
                dataConversionLocation='path-choice'
                dataConversionSalesPath='managed'
              >{content.paths.managed.action}</Action>
              <div className={styles.contactLinks} aria-label='Direct streaming production contacts'>
                <Action variant='plain' href='tel:+13059744411' external icon={FiPhone} onClick={() => track('live_streaming_path_call', 15, 'live_streaming_path_choice')}>Call</Action>
                <Action variant='plain' href='mailto:info@miamisoundrental.com' external icon={FiMail} onClick={() => track('live_streaming_path_email', 15, 'live_streaming_path_choice')}>Email</Action>
                <Action variant='plain' icon={FiMessageCircle} onClick={openChat}>Chat</Action>
              </div>
            </article>
            <article className={styles.pathCard}>
              <span>{content.paths.rental.label}</span>
              <h3>{content.paths.rental.title}</h3>
              <p>{content.paths.rental.description}</p>
              <Action variant='secondary' size='large' href='#streaming-bundles' focusTargetOnNavigate icon={FiArrowRight} dataConversionChannel='catalog' dataConversionKey='live-streaming.bundles' dataConversionLocation='path-choice' dataConversionSalesPath='self_service'>
                {content.paths.rental.action}
              </Action>
              <Action variant='plain' href='/visuals' icon={FiArrowRight} onClick={() => track('live_streaming_visual_equipment', 2, 'live_streaming_path_choice')}>Browse individual visual equipment</Action>
            </article>
          </div>
        </Container>
      </section>

      <section className={styles.eventTypes} aria-labelledby='streaming-event-types-title'>
        <Container>
          <SectionHeading id='streaming-event-types-title' {...content.eventTypes} />
          <div className={styles.eventTypeGrid}>
            {content.eventTypes.items.map((item, index) => {
              const Icon = eventTypeIcons[index % eventTypeIcons.length]
              return <article key={item.title}><Icon aria-hidden='true' /><h3>{item.title}</h3><p>{item.description}</p></article>
            })}
          </div>
        </Container>
      </section>

      <section className={styles.capabilities} aria-labelledby='streaming-capabilities-title'>
        <Container>
          <SectionHeading id='streaming-capabilities-title' inverse {...content.capabilities} />
          <div className={styles.capabilityGrid}>
            {content.capabilities.items.map(item => {
              const Icon = capabilityIcons[item.icon] || FiCheck
              return <article key={item.title}><span><Icon aria-hidden='true' /></span><h3>{item.title}</h3><p>{item.description}</p></article>
            })}
          </div>
        </Container>
      </section>

      <section className={styles.processSection} aria-labelledby='streaming-process-title'>
        <Container className={styles.splitLayout}>
          <div className={styles.splitMedia}><Media src={content.process.image} alt={content.process.imageAlt} ratio='4 / 5' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} /></div>
          <div className={styles.splitCopy}>
            <SectionHeading id='streaming-process-title' eyebrow={content.process.eyebrow} title={content.process.title} description={content.process.description} />
            <ol className={styles.processList}>
              {content.process.steps.map((step, index) => <li key={step.title}><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{step.title}</h3><p>{step.description}</p></div></li>)}
            </ol>
          </div>
        </Container>
      </section>

      <section className={styles.venue} aria-labelledby='streaming-readiness-title'>
        <Container className={styles.venueLayout}>
          <div className={styles.venueCopy}>
            <SectionHeading id='streaming-readiness-title' eyebrow={content.risk.eyebrow} title={content.risk.title} description={content.risk.description} />
            <ul>{content.risk.items.map(item => <li key={item}><FiCheck aria-hidden='true' /><span>{item}</span></li>)}</ul>
            <div className={styles.venueActions}>
              <Action onClick={() => openProposal('live_streaming_readiness')} icon={FiArrowRight}>Discuss broadcast readiness</Action>
            </div>
          </div>
          <Media src={content.risk.image} alt={content.risk.imageAlt} ratio='5 / 4' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
        </Container>
      </section>

      <section className={styles.caseStudies} aria-labelledby='streaming-case-studies-title'>
        <Container>
          <SectionHeading id='streaming-case-studies-title' eyebrow='Documented streaming work' title='Real South Florida events with live-streaming scope' description='These examples use authentic MSR project photography and documented equipment—not stock images or generic broadcast claims.' />
          <div className={`${styles.caseGrid} ${localStyles.caseGrid}`}>
            {content.caseStudies.map(project => (
              <article className={styles.caseCard} key={project.slug}>
                <Media src={project.image} alt={project.imageAlt} ratio='4 / 3' policy={MEDIA_POLICY_KEYS.CARD} />
                <div className={styles.caseCopy}>
                  <span>{project.type}</span><h3>{project.title}</h3><p>{project.description}</p>
                  <ul aria-label={`${project.title} production facts`}>{project.facts.map(fact => <li key={fact}>{fact}</li>)}</ul>
                  <Action variant='plain' href={`/portfolio/${project.slug}`} icon={FiArrowRight} dataConversionChannel='other' dataConversionKey='live-streaming.case-study' dataConversionLocation='case-studies' dataConversionSalesPath='managed' onClick={() => track('live_streaming_case_study', 2, project.slug)}>View documented project</Action>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <ReviewProof eyebrow='Verified Google feedback' heading='Feedback relevant to streaming, conferences, cameras, and production' pagePath='/production-services/live-streaming' initialData={reviewData} sectionId='live-streaming-reviews' />

      <FAQGroup questions={content.faqs} eyebrow='Broadcast planning questions' title='Live streaming production FAQs' intro='Clear answers about managed production, platforms, connectivity, remote presenters, recording, equipment rental, timing, and service coverage.' headingId='live-streaming-faqs' />

      <section className={styles.proposal} aria-labelledby='streaming-proposal-title'>
        <Container className={styles.proposalLayout}>
          <div className={styles.proposalIntro}>
            <SectionHeading id='streaming-proposal-title' eyebrow={content.proposal.eyebrow} title={content.proposal.title} description={content.proposal.description} />
            <div className={styles.proposalNotes}>
              <span><FiClock aria-hidden='true' /> Share what you know now; the technical workflow can be advanced next.</span>
              <span><FiMapPin aria-hidden='true' /> Miami, Fort Lauderdale, and South Florida.</span>
              <span><FiRadio aria-hidden='true' /> Reviewed by a production team, not a generic equipment queue.</span>
            </div>
          </div>
          <div className={styles.proposalFormCard}><CorporateProposalForm idPrefix='live-streaming-proposal-inline' mode='streaming' /></div>
        </Container>
      </section>

      <section className={styles.builderBand} aria-labelledby='streaming-builder-title'>
        <Container className={styles.builderLayout}>
          <div><span>{content.builder.eyebrow}</span><h2 id='streaming-builder-title'>{content.builder.title}</h2><p>{content.builder.description}</p></div>
          <Action variant='secondary' size='large' icon={FiCalendar} onClick={() => openBuilder('live_streaming_late_builder')} dataConversionChannel='builder' dataConversionKey='live-streaming.late-builder' dataConversionLocation='late-builder' dataConversionSalesPath='self_service'>{content.builder.action}</Action>
        </Container>
      </section>

      <section className={localStyles.bundleIntro} aria-labelledby='streaming-bundles-heading'>
        <Container>
          <SectionHeading id='streaming-bundles-heading' eyebrow={content.bundles.eyebrow} title={content.bundles.title} description={content.bundles.description} />
        </Container>
      </section>
      <div id='streaming-bundles' className={localStyles.catalogAnchor}>
        <ProductCatalog products={bundles} listName='Live Streaming Bundles' eyebrow='Compare equipment bundles' pageSize={3} showProductDirectory={false} />
        <Container className={localStyles.catalogAction}>
          <Action variant='secondary' href='/visuals' icon={FiArrowRight} dataConversionChannel='catalog' dataConversionKey='live-streaming.full-equipment' dataConversionLocation='bundle-catalog' dataConversionSalesPath='self_service'>{content.bundles.equipmentAction}</Action>
        </Container>
      </div>

      <section className={styles.related} aria-labelledby='streaming-related-title'>
        <Container>
          <SectionHeading id='streaming-related-title' eyebrow={content.related.eyebrow} title={content.related.title} />
          <div className={styles.relatedGrid}>
            {content.related.items.map(item => {
              const href = item.href === '/equipment/visuals' ? '/visuals' : item.href
              return <article key={href}><h3>{item.title}</h3><p>{item.description}</p><Action variant='plain' href={href} icon={FiArrowRight}>Open {item.title}</Action></article>
            })}
          </div>
        </Container>
      </section>
    </PageShell>
  )
}

export default LiveStreamingProductionPage
